import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Check, Heart, MapPin, MessageSquareText, Radar, Search, Sparkles, Star, TrendingUp, Users, Zap, Clapperboard, Image as ImageIcon, Wand2, Shuffle, Music2, ShieldCheck } from "lucide-react";
import { Reveal, Counter, Marquee, TikTokPhone, InstaCard, creators, reels, igPosts, PlatformBadge } from "@/components/marketing/kit";
import { TikTokIcon, InstagramIcon } from "@/components/marketing/icons";

const TABS = [
  { id: "discover", label: "AI Discovery", icon: <Search />, title: "Find creators by signal, not vanity metrics", body: "Search 180M+ TikTok & Instagram creators with AI match scoring that weighs audience quality, engagement, niche fit, safety and price.", points: ["AI fit score per brief", "Fake-follower detection", "Estimated collab pricing"] },
  { id: "pipeline", label: "CRM Pipeline", icon: <Users />, title: "A living creator pipeline your whole team owns", body: "Drag creators from sourced to signed. Keep notes, tags, rates and campaign history in one shared relationship database.", points: ["Kanban relationship stages", "Shared notes & tags", "Full collaboration history"] },
  { id: "briefs", label: "AI Briefs", icon: <Sparkles />, title: "Turn a product idea into a full brief in seconds", body: "Describe the campaign and let GPT draft objectives, deliverables, hooks, do's & don'ts tailored to each creator and platform.", points: ["Platform-aware deliverables", "Hook & angle suggestions", "Editable in one click"] },
  { id: "outreach", label: "AI Outreach", icon: <MessageSquareText />, title: "Personalized outreach that never sounds mass-sent", body: "Generate DMs and emails that reference the creator's content, audience and the exact reason they fit your campaign.", points: ["Creator-aware personalization", "Tone controls", "Reusable sequences"] },
  { id: "listening", label: "Social Listening", icon: <Radar />, title: "Catch trends and mentions before they peak", body: "Monitor hashtags, brands, competitors and categories. See volume, sentiment and the emerging creators driving the conversation.", points: ["Keyword & competitor monitors", "Live sentiment direction", "Trend discovery"] },
];

const NICHES = [{ k: "Beauty", cpm: 11 }, { k: "Fashion", cpm: 9 }, { k: "Gaming", cpm: 7 }, { k: "Fitness", cpm: 8 }, { k: "Food", cpm: 6 }, { k: "Tech", cpm: 13 }];
const SHOTS = {
  discover: { src: "/shots/discover.jpg", alt: "HypeFlow AI creator discovery — TikTok & Instagram creators ranked by fit" },
  pipeline: { src: "/shots/connect.jpg", alt: "HypeFlow creator CRM relationship pipeline" },
  briefs: { src: "/shots/campaign.jpg", alt: "HypeFlow campaign workspace with auto-computed reach and EMV" },
  outreach: { src: "/shots/outreach.jpg", alt: "HypeFlow AI outreach studio generating a personalized creator message" },
  listening: { src: "/shots/listening.jpg", alt: "HypeFlow social listening monitors with live mentions and sentiment" },
};
const TESTI = [
  { q: "Discovery, outreach, approvals and reporting finally feel like one process — not five tools. We ship campaigns 3x faster.", n: "Maya R.", r: "Head of Influencer, DTC beauty", img: creators[0].avatar, m: "+312%", ms: "reach" },
  { q: "The AI match scores are scary good. Our shortlist went from a week of research to about ten minutes.", n: "James L.", r: "Agency Partner, Amsterdam", img: creators[1].avatar, m: "9min", ms: "to shortlist" },
  { q: "We can finally prove EMV and ROI to clients with live dashboards. It's become our pitch-winning weapon.", n: "Priya S.", r: "Growth Lead, APAC retail", img: creators[2].avatar, m: "4.8x", ms: "ROI" },
];

const STUDIO = [
  { icon: <Wand2 />, title: "AI Creative Director", body: "Chat with a top AI writer (Claude, GPT or Gemini) like in ChatGPT. It keeps one living story you can edit, then turns it into scenes. Your own script stays word for word." },
  { icon: <Music2 />, title: "Music Video Studio", body: "Add your song and lyrics. Scenes illustrate the words, cuts land on lyric lines and beats, and the Director can build the whole story from your lyrics." },
  { icon: <ImageIcon />, title: "Image Studio", body: "About 60 image models, including Nano Banana, GPT Image 2, FLUX.2 and Seedream. Approve every starting frame before any video is made." },
  { icon: <Clapperboard />, title: "Video Studio", body: "Every OpenRouter video model, from Veo to Kling and MiniMax H3 Max, with the same characters kept in every scene and no split screens." },
  { icon: <Shuffle />, title: "Smart Remix & Edit my footage", body: "Remix a winning ad, or cut your own footage to the music with colour matching and reusable editing presets." },
  { icon: <ShieldCheck />, title: "Safe, resumable production", body: "Videos keep rendering when you close the app, resume after server restarts and never charge twice. Pay only what is used." },
];

function Panel({ tab }) {
  const s = SHOTS[tab] || SHOTS.discover;
  return (
    <div className="mk-shot" data-testid={`sandbox-${tab}`}>
      <div className="mk-shot-bar"><i /><i /><i /></div>
      <img src={s.src} alt={s.alt} loading="lazy" />
    </div>
  );
}

export default function Home() {
  const [tab, setTab] = useState("discover");
  const [budget, setBudget] = useState(12000);
  const [niche, setNiche] = useState("Beauty");
  const calc = useMemo(() => {
    const cpm = NICHES.find(n => n.k === niche).cpm;
    const impressions = Math.round((budget / cpm) * 1000);
    return { impressions, engagements: Math.round(impressions * 0.072), conversions: Math.round(impressions * 0.072 * 0.038), emv: Math.round(budget * 4.8) };
  }, [budget, niche]);
  const fmt = n => n.toLocaleString();
  const active = TABS.find(t => t.id === tab);

  return (
    <main>
      {/* HERO */}
      <section className="mk-hero" data-testid="marketing-hero">
        <div className="mk-wrap mk-hero-grid">
          <div className="mk-hero-copy">
            <Reveal><span className="mk-pill" data-testid="hero-kicker"><span className="dot" /> Now live · AI creator matching</span></Reveal>
            <Reveal delay={0.08}><h1 data-testid="hero-title">Find the right creators to <em>grow your brand and drive sales.</em></h1></Reveal>
            <Reveal delay={0.16}><p data-testid="hero-description">HypeFlow AI helps you find TikTok and Instagram creators, manage collaborations, and measure results — then generate on-brand content with the built-in AI Creative Studio. Discover creators and create with AI, all in one place.</p></Reveal>
            <Reveal delay={0.24}><div className="mk-hero-actions">
              <Link to="/login?mode=register" className="mk-btn primary lg" data-testid="hero-start-trial">Start free <ArrowRight size={17} /></Link>
              <Link to="/how-it-works" className="mk-btn ghost lg" data-testid="hero-explore-features">See how it works</Link>
            </div></Reveal>
            <Reveal delay={0.32}><div className="mk-hero-trust">
              <div className="mk-avatars">{creators.slice(0, 5).map(c => <img key={c.id} src={`${c.avatar}&w=80&h=80&fit=crop`} alt="" />)}</div>
              <p><span className="stars">★★★★★</span><br /><b>2,400+ brands & agencies</b> growing with HypeFlow</p>
            </div></Reveal>
          </div>

          <div className="mk-hero-visual">
            <div className="mk-hero-glow" />
            <div style={{ position: "relative", zIndex: 2 }}><TikTokPhone post={reels[0]} /></div>
            <Reveal delay={0.4} y={0} className="mk-float mk-float-tl" data-testid="hero-float-match">
              <div className="mf-row"><span className="mf-ic cyan"><Sparkles size={18} /></span>
                <div><small>AI match score</small><b className="grad-text">97% fit</b></div></div>
            </Reveal>
            <Reveal delay={0.55} y={0} className="mk-float mk-float-br" data-testid="hero-float-roi">
              <div className="mf-row"><span className="mf-ic mint"><TrendingUp size={18} /></span>
                <div><small>Campaign ROI</small><b>4.8×</b><span className="trend"><TrendingUp size={12} /> +38% vs last</span></div></div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="mk-sec" style={{ padding: "10px 0 70px" }}>
        <div className="mk-wrap" style={{ textAlign: "center", marginBottom: 26 }}>
          <span className="mk-pill"><span className="dot" /> Trending creators on the platform right now</span>
        </div>
        <Marquee speed={42}>
          {creators.map(c => (
            <div className="mk-chip" key={c.id}><img src={`${c.avatar}&w=100&h=100&fit=crop`} alt="" />
              <div><div className="c-name">{c.name} {c.platform === "tiktok" ? <TikTokIcon size={12} /> : <InstagramIcon size={12} />}</div>
                <div className="c-sub">{c.followers} · {c.engagement}</div></div></div>
          ))}
        </Marquee>
      </section>

      {/* STATS */}
      <section className="mk-stats"><div className="mk-wrap"><div className="mk-stats-grid">
        {[[180, "M+", "creators indexed", 0], [8.4, "M+", "signals analysed daily", 1], [91, "%", "avg match confidence", 0], [4.8, "×", "average campaign ROI", 1]].map(([v, s, l, d], i) => (
          <Reveal key={l} delay={i * 0.08} data-testid={`stat-${i}`}><div><b><Counter to={v} suffix={s} decimals={d} /></b><span>{l}</span></div></Reveal>
        ))}
      </div></div></section>

      {/* REAL PRODUCT SHOWCASE */}
      <section className="mk-sec" style={{ paddingTop: 0 }} data-testid="home-product-showcase">
        <div className="mk-wrap">
          <div className="mk-head"><span className="mk-pill"><Sparkles size={14} /> Inside the platform</span>
            <h2 className="mk-h2">This is the actual product — not a mockup</h2>
            <p>Your command center: active campaigns, portfolio reach, earned media value and the creators breaking through, live.</p></div>
          <Reveal delay={0.1}><div className="mk-tour-hero" style={{ marginTop: 10 }}>
            <div className="mk-shot" data-testid="home-shot-overview">
              <div className="mk-shot-bar"><i /><i /><i /></div>
              <img src="/shots/overview.jpg" alt="HypeFlow command center dashboard with live campaign momentum and creator radar" loading="lazy" />
            </div>
          </div></Reveal>
        </div>
      </section>

      {/* FEATURE SANDBOX */}
      <section className="mk-sec" data-testid="feature-sandbox">
        <div className="mk-wrap">
          <div className="mk-head"><span className="mk-pill"><Zap size={14} /> The platform</span>
            <h2 className="mk-h2">One workspace for the entire creator journey</h2>
            <p>Click through the exact tools your team will use every day.</p></div>
          <div className="mk-sandbox-tabs">
            {TABS.map(t => <button key={t.id} className={`mk-tab${tab === t.id ? " on" : ""}`} onClick={() => setTab(t.id)} data-testid={`sandbox-tab-${t.id}`}>{t.icon}<span>{t.label}</span></button>)}
          </div>
          <div className="mk-sandbox-panel">
            <div className="mk-sandbox-copy">
              <h3>{active.title}</h3><p>{active.body}</p>
              <ul>{active.points.map(p => <li key={p}><Check /> {p}</li>)}</ul>
              <Link to="/features" className="mk-btn ghost" data-testid="sandbox-learn-more">Explore all features <ArrowRight size={16} /></Link>
            </div>
            <div className="mk-sandbox-visual" style={{ display: "block", minHeight: 0 }}><Panel tab={tab} /></div>
          </div>
        </div>
      </section>

      {/* AI CREATIVE STUDIO */}
      <section className="mk-sec mk-studio-sec" data-testid="home-creative-studio">
        <div className="mk-wrap">
          <div className="mk-head"><span className="mk-pill"><Sparkles size={14} /> New · AI Creative Studio</span>
            <h2 className="mk-h2">Don't just find creators — <span className="grad-text">create with AI</span></h2>
            <p>HypeFlow AI now ships a full creative studio for ads and cinematic music videos. Chat your story with an AI director, sync it to your song and lyrics, and generate it with the newest image and video models, without leaving your workspace.</p></div>
          <div className="mk-studio-grid three">
            {STUDIO.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}><div className="mk-studio-card" data-testid={`studio-card-${s.title.toLowerCase().replace(/\s/g, "-")}`}>
                <span className="mk-studio-ic">{s.icon}</span>
                <div className="mk-studio-live"><i /> Available now</div>
                <h3>{s.title}</h3><p>{s.body}</p>
              </div></Reveal>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 36 }}>
            <Link to="/features" className="mk-btn primary lg" data-testid="studio-explore">Explore the Creative Studio <ArrowRight size={17} /></Link>
          </div>
        </div>
      </section>

      {/* REELS SHOWCASE */}
      <section className="mk-sec" style={{ background: "rgba(17,22,34,.5)", borderBlock: "1px solid var(--line)" }} data-testid="reels-showcase">
        <div className="mk-wrap">
          <div className="mk-head"><span className="mk-pill"><span className="dot" /> Live content feed</span>
            <h2 className="mk-h2">See real creator content, the way audiences do</h2>
            <p>Preview TikToks and Instagram Reels from matched creators without leaving HypeFlow.</p></div>
          <div className="mk-reels-grid">
            {reels.map((r, i) => <Reveal key={r.handle} delay={(i % 4) * 0.08}><TikTokPhone post={r} compact /></Reveal>)}
          </div>
          <div className="mk-ig-strip">
            {igPosts.map((p, i) => <Reveal key={p.handle} delay={i * 0.08}><InstaCard post={p} /></Reveal>)}
          </div>
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <section className="mk-sec" data-testid="roi-calculator">
        <div className="mk-wrap">
          <div className="mk-head"><span className="mk-pill"><BarChart3 size={14} /> ROI estimator</span>
            <h2 className="mk-h2">Model your next campaign in real time</h2>
            <p>Drag the budget, pick a niche, and watch projected impact update instantly.</p></div>
          <div className="mk-roi">
            <div className="mk-roi-controls">
              <div className="mk-roi-field">
                <label>Campaign budget <span className="val">${fmt(budget)}</span></label>
                <input type="range" min="1000" max="50000" step="500" value={budget} onChange={e => setBudget(+e.target.value)} data-testid="roi-budget-slider" />
              </div>
              <div className="mk-roi-field">
                <label>Creator niche</label>
                <div className="mk-roi-niche">{NICHES.map(n => <button key={n.k} className={niche === n.k ? "on" : ""} onClick={() => setNiche(n.k)} data-testid={`roi-niche-${n.k.toLowerCase()}`}>{n.k}</button>)}</div>
              </div>
            </div>
            <div className="mk-roi-result" data-testid="roi-result">
              <h4>Projected campaign impact</h4>
              <div className="big" data-testid="roi-impressions">{fmt(calc.impressions)}</div>
              <small style={{ color: "var(--tx3)" }}>estimated impressions</small>
              <div className="mk-roi-metrics">
                <div><b>{fmt(calc.engagements)}</b><small>engagements</small></div>
                <div><b>{fmt(calc.conversions)}</b><small>est. conversions</small></div>
                <div><b>${fmt(calc.emv)}</b><small>earned media value</small></div>
                <div><b>4.8×</b><small>benchmark ROI</small></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mk-sec" data-testid="marketing-testimonials">
        <div className="mk-wrap">
          <div className="mk-head"><span className="mk-pill"><Star size={14} /> Social proof</span>
            <h2 className="mk-h2">Teams that switched aren't going back</h2></div>
          <div className="mk-quotes-grid">
            {TESTI.map((t, i) => (
              <Reveal key={t.n} delay={i * 0.08}><div className="mk-quote">
                <div className="stars">★★★★★</div><p>"{t.q}"</p>
                <div className="who"><img src={`${t.img}&w=90&h=90&fit=crop`} alt="" />
                  <div><b>{t.n}</b><span>{t.r}</span></div>
                  <div className="metric"><b>{t.m}</b><span>{t.ms}</span></div></div>
              </div></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="mk-sec" data-testid="pricing-teaser" style={{ background: "rgba(17,22,34,.5)", borderBlock: "1px solid var(--line)" }}>
        <div className="mk-wrap">
          <div className="mk-head"><span className="mk-pill">Simple pricing</span>
            <h2 className="mk-h2">Four plans. Start free, scale when ready.</h2>
            <p>From solo brands to global agencies — pick the pace of your creator program.</p></div>
          <div style={{ display: "flex", justifyContent: "center", gap: 14, flexWrap: "wrap" }}>
            {[["Starter", "$99"], ["Growth", "$249"], ["Scale", "$599"], ["Enterprise", "Custom"]].map(([n, p]) => (
              <div key={n} style={{ background: "var(--card)", border: "1px solid var(--line)", borderRadius: 14, padding: "18px 26px", textAlign: "center", minWidth: 140 }}>
                <div style={{ fontSize: 13, color: "var(--tx3)" }}>{n}</div>
                <div className="mono" style={{ fontSize: 26, fontWeight: 700 }}>{p}{p !== "Custom" && <span style={{ fontSize: 12, color: "var(--tx3)" }}>/mo</span>}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 34 }}>
            <Link to="/pricing" className="mk-btn primary lg" data-testid="home-view-pricing">Compare all plans <ArrowRight size={17} /></Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mk-sec" data-testid="home-final-cta"><div className="mk-wrap"><div className="mk-cta">
        <span className="mk-pill" style={{ margin: "0 auto" }}><Sparkles size={14} /> Your next viral campaign</span>
        <h2>Find creators your audience already believes.</h2>
        <p>Start free and turn creator marketing into a repeatable growth engine.</p>
        <div className="mk-cta-actions">
          <Link to="/login?mode=register" className="mk-btn primary lg" data-testid="home-final-start-trial">Start free <ArrowRight size={17} /></Link>
          <Link to="/contact" className="mk-btn ghost lg" data-testid="home-final-demo">Book a demo</Link>
        </div>
      </div></div></section>
    </main>
  );
}
